import { Component, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgForm, NgModel } from '@angular/forms';
import {
  DestinationsService,
  Destination,
  CreateDestinationRequest,
  DestinationConfig,
  CustomerRoleCustomerS3BucketDestinationConfig,
  CustomerRoleInternalS3BucketDestinationConfig,
  InternalRoleCustomerS3BucketDestinationConfig,
  CustomerServiceAccountCustomerGcsBucketDestinationConfig,
  SnowflakeDestinationConfig,
} from 'ldt-data-deliveries-api';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { Router } from '@angular/router';
import { TestStatusBadgeComponent } from 'src/app/shared/test-status-badge/test-status-badge.component';
import { timer } from 'rxjs';
import { Integration } from '../available-integrations.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-integration-dialog',
  templateUrl: './integration-dialog.component.html',
  styleUrls: ['./integration-dialog.component.scss'],
  host: { class: 'ldt-modal-component' },
  standalone: false,
})
export class IntegrationDialogComponent {
  @ViewChild('form1A') form1A: NgForm;
  @ViewChild('form1B') form1B: NgForm;
  @ViewChild('form2A') form2A: NgForm;
  @ViewChild('formGCS') formGCS: NgForm;
  @ViewChild('formSnowflake') formSnowflake: NgForm;

  constructor(
    public dialogRef: MatDialogRef<IntegrationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { orgId: string; integration: Integration },
    private destinationsService: DestinationsService,
    private notify: NotificationService,
    private router: Router
  ) {
    this.orgId = data.orgId; // this has to be passed from the parent component, can't be accessed directly from dialog
    this.integration = data.integration;
  }

  orgId: string;
  integration: Integration;
  stepperSteps: string[];
  activeStepperStep = 1;
  testStatus: string = 'Unknown';
  testStatusLabel: string = 'Destination Status';
  isLoading: boolean = false;
  @ViewChild(TestStatusBadgeComponent) testStatusBadgeComponent!: TestStatusBadgeComponent;
  showTimeStamp: boolean = false;
  createdDestination: Destination | undefined;
  newIntegration: boolean = true;
  creationTimeout: boolean = false;
  activeForm: NgForm | undefined;
  isSubmitting: boolean = false;

  // form 2A (LDT S3 bucket form)
  @ViewChild('roleArnInput') roleArnInput!: NgModel;

  // Snowflake form
  @ViewChild('accountNameInput') accountNameInput!: NgModel;

  // GCS form variables
  gcpServiceAccountFile: File | null = null;
  gcpServiceAccountContent: string = '';

  // Environment constants for template access
  readonly GCS_DESTINATION_TYPE_ID = environment.GCS_DESTINATION_TYPE_ID;
  readonly SNOWFLAKE_DESTINATION_TYPE_ID = environment.SNOWFLAKE_DESTINATION_TYPE_ID;
  getFormattedS3Uri(destinationId: number): string {
    const formattedOrgId = this.orgId.replace('_', '-');
    return `s3://arn:aws:s3:us-east-1:100811426269:accesspoint/${formattedOrgId}-${destinationId}/${this.orgId}/`;
  }

  getFormattedAccessPointARN(destinationId: number): string {
    const formattedOrgId = this.orgId.replace('_', '-');
    return `arn:aws:s3:us-east-1:100811426269:accesspoint/${formattedOrgId}-${destinationId}`;
  }

  showInit: boolean = false;
  showChoice1: boolean = false;
  showChoice1A: boolean = false;
  showChoice1B: boolean = false;
  showChoice2: boolean = false;
  showGCS: boolean = false;
  showSnowflake: boolean = false;
  successView: boolean = false;

  ngOnInit(): void {
    this.setStepperStep(this.integration.name);
    this.setFirstView(this.integration.name);
  }

  setStepperStep(destinationName: string): void {
    if (destinationName === 'AWS S3') {
      this.stepperSteps = ['Choose S3 Bucket Type', 'Configure S3 Bucket', 'Setup Complete'];
    } else if (destinationName === 'Google Cloud Storage') {
      this.stepperSteps = ['Configure GCS Bucket', 'Setup Complete'];
    } else if (destinationName === 'Snowflake') {
      this.stepperSteps = ['Configure Snowflake', 'Setup Complete'];
    } else {
      this.stepperSteps = ['Configure Destination', 'Setup Complete'];
    }
  }

  setFirstView(destinationName: string): void {
    if (destinationName === 'AWS S3') {
      this.showInit = true;
    } else if (destinationName === 'Google Cloud Storage') {
      this.showGCS = true;
      this.activeStepperStep = 1;
      setTimeout(() => {
        this.activeForm = this.formGCS;
      });
    } else if (destinationName === 'Snowflake') {
      this.showSnowflake = true;
      this.activeStepperStep = 1;
      setTimeout(() => {
        this.activeForm = this.formSnowflake;
      });
    } else {
      this.notify.error('Something went wrong.');
    }
  }

  // Choice 1: User S3 bucket OR LDT S3 bucket
  handleChoice1() {
    this.showChoice1 = true;
    this.showChoice2 = false;
    this.showInit = false;
    this.activeStepperStep = 1;
  }

  // Choice 1A: User S3 bucket --> Grant Access to LDT Role
  handleChoice1A() {
    this.showInit = false;
    this.showChoice1 = false;
    this.showChoice1A = true; // show this only
    this.showChoice1B = false;
    this.activeStepperStep = 2;
    setTimeout(() => {
      this.activeForm = this.form1A;
    });
  }

  // Choice 1B: User S3 bucket --> Create Cross-account role & allow access to LDT
  handleChoice1B() {
    this.showInit = false;
    this.showChoice1 = false;
    this.showChoice1A = false;
    this.showChoice1B = true;
    this.activeStepperStep = 2;
    setTimeout(() => {
      this.activeForm = this.form1B;
    });
  }

  // Choice 2: LDT S3 bucket form
  handleChoice2() {
    this.showChoice2 = true;
    this.showChoice1 = false;
    this.showChoice1A = false;
    this.showChoice1B = false;
    this.activeStepperStep = 2;
    setTimeout(() => {
      this.activeForm = this.form2A;
    });
  }

  isFormValid(): boolean {
    return (this.activeForm && this.activeForm.valid) || false;
  }

  submitActiveForm() {
    if (this.activeForm) {
      var destType;
      if (this.activeForm === this.form1A) {
        destType = 3;
      } else if (this.activeForm === this.form1B) {
        destType = 2;
      } else if (this.activeForm === this.form2A) {
        destType = 1;
      } else if (this.activeForm === this.formGCS) {
        destType = environment.GCS_DESTINATION_TYPE_ID;
      } else if (this.activeForm === this.formSnowflake) {
        destType = environment.SNOWFLAKE_DESTINATION_TYPE_ID;
      } else {
        return;
      }

      this.handleDestinationSubmit(this.activeForm, destType);
    }
  }

  /**
   * Handles the GCP service account JSON file selection
   * @param event - The file input change event
   */
  onGcpFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            JSON.parse(content); // Validate JSON
            this.gcpServiceAccountContent = content;
            this.gcpServiceAccountFile = file;
          } catch (error) {
            this.notify.error('Invalid JSON file. Please select a valid GCP service account key.');
            event.target.value = '';
            this.gcpServiceAccountContent = '';
            this.gcpServiceAccountFile = null;
          }
        };
        reader.readAsText(file);
      } else {
        this.notify.error('Please select a JSON file');
        event.target.value = '';
        this.gcpServiceAccountContent = '';
        this.gcpServiceAccountFile = null;
      }
    }
  }
  /**
   * Handles the form submission to crate a destination.
   *
   * @param form - The NgForm object representing the form being submitted.
   * @returns void
   */
  handleDestinationSubmit(form: NgForm, destination_type_id: number): void {
    this.isSubmitting = true;
    this.showTimeStamp = false;

    let destinationConfig:
      | DestinationConfig
      | CustomerRoleCustomerS3BucketDestinationConfig
      | CustomerRoleInternalS3BucketDestinationConfig
      | InternalRoleCustomerS3BucketDestinationConfig
      | CustomerServiceAccountCustomerGcsBucketDestinationConfig
      | SnowflakeDestinationConfig;

    if (destination_type_id === 1) {
      destinationConfig = {
        role_arn: form.value.role_arn,
      };
    } else if (destination_type_id === 2) {
      destinationConfig = {
        bucket_identifier: form.value.bucket_identifier,
        role_arn: form.value.role_arn,
        external_id: form.value.external_id,
        bucket_key: form.value.bucket_key != '' ? form.value.bucket_key : undefined,
      };
    } else if (destination_type_id === 3) {
      destinationConfig = {
        bucket_identifier: form.value.bucket_identifier,
        bucket_key: form.value.bucket_key != '' ? form.value.bucket_key : undefined,
      };
    } else if (destination_type_id === environment.GCS_DESTINATION_TYPE_ID) {
      if (!this.gcpServiceAccountContent) {
        this.notify.error('Please upload a GCP service account JSON file');
        this.isSubmitting = false;
        return;
      }
      destinationConfig = {
        service_account_key: this.gcpServiceAccountContent,
        bucket_identifier: form.value.bucket_identifier,
        bucket_key: form.value.bucket_key != '' ? form.value.bucket_key : undefined,
      };
    } else if (destination_type_id === environment.SNOWFLAKE_DESTINATION_TYPE_ID) {
      destinationConfig = {
        account_name: form.value.account_name,
      };
    } else {
      this.notify.error('Error creating destination - invalid destination type');
      return;
    }

    // We need to use `any` here because the schema is using `anyOf` instead of `oneOf` to avoid generated python client issues, and that creates an isse with the TS generator
    let submissionData: CreateDestinationRequest | any = {
      name: form.value.name, // will this work for all three forms?
      destination_type_id: destination_type_id,
      config: destinationConfig,
    };

    if (form.valid) {
      this.destinationsService.createDestination(this.orgId, submissionData).subscribe({
        next: (data: Destination) => {
          this.createdDestination = data;
          this.isLoading = false;
          this.notify.success('Data delivery destination created.');
          this.successView = true;
          this.activeStepperStep = 3;
          this.showInit = false;
          this.showChoice1 = false;
          this.showChoice1A = false;
          this.showChoice1B = false;
          this.showChoice2 = false;
          this.showGCS = false;
          this.showSnowflake = false;
          this.isSubmitting = false;

          // For the cross-account type, disable the test button for a few seconds while config finishes
          if (destination_type_id === 2) {
            this.creationTimeout = true;

            timer(5000).subscribe(() => {
              this.creationTimeout = false;
            });
          }
        },
        error: (error) => {
          console.error('Error creating destination:', error);
          this.isLoading = false;
          this.isSubmitting = false;
          let errorMessage = error.error?.message || 'Error creating destination.';
          if (error.status === 400 && error.error?.detail?.role_arn === 'Invalid') {
            errorMessage = 'Invalid IAM Role ARN. Are you sure this role exists?';
            // invalidate the role ARN input field so user can see the error
            this.roleArnInput.control.setErrors({ serverError: errorMessage });
          } else if (
            error.status === 400 &&
            destination_type_id === environment.SNOWFLAKE_DESTINATION_TYPE_ID &&
            error.error?.detail &&
            typeof error.error.detail === 'object'
          ) {
            const detail = error.error.detail as Record<string, string>;
            const messages = Object.entries(detail).map(
              ([field, reason]) => `${field}: ${reason}`
            );
            errorMessage = messages.length
              ? `Invalid Snowflake configuration — ${messages.join(', ')}`
              : errorMessage;
            if (detail.account_name && this.accountNameInput) {
              this.accountNameInput.control.setErrors({
                serverError: `Account Name: ${detail.account_name}`,
              });
            }
          } else {
            errorMessage = error.error?.message || errorMessage;
          }

          this.notify.error(errorMessage);
        },
      });
    } else {
      console.error('Error submitting destination form:', form.errors);
      this.notify.error('Form is not valid.');
    }
  }

  /**
   * Tests the status of a destination.
   * @param orgId - The ID of the organization.
   * @param destinationData - destination object just created.
   */
  testDestinationStatus(destinationData: Destination) {
    this.showTimeStamp = true;
    this.isLoading = true;
    this.testStatus = 'Loading...';

    if (!destinationData) {
      this.notify.error(
        'Something went wrong with getting the destination status. Please try again later.'
      );
      return;
    }

    this.destinationsService.testDestination(destinationData.org_id, destinationData.id).subscribe({
      next: (data) => {
        this.isLoading = false;
        if (data.status) {
          this.testStatus = data.status;
          this.testStatusBadgeComponent.updateStatusBadge(data.status);
          this.testStatusBadgeComponent.updateTimeStamp(new Date().toISOString());
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.testStatus = error.error?.message || 'Error testing destination.';
        this.testStatusBadgeComponent.updateTimeStamp(destinationData.tested_at || '');
      },
    });
  }

  closeDialog() {
    this.dialogRef.close({});
    setTimeout(() => {
      this.goToConfiguredIntegrations(); // navigate to the configured integrations page
    }, 200);
  }

  goBackToInit() {
    this.showInit = true;
    this.showChoice1 = false;
    this.showChoice1A = false;
    this.showChoice1B = false;
    this.showChoice2 = false;
    this.activeStepperStep = 1;
  }

  goBack() {
    if (!this.showChoice2 && this.activeStepperStep == 2) {
      this.goBackToCard2();
      return;
    }

    this.goBackToInit();
  }

  goBackToCard2() {
    this.showChoice1 = true;
    this.showChoice1B = false;
    this.showChoice1A = false;

    this.showChoice2 = false;
    this.activeStepperStep = 1;
  }

  goToConfiguredIntegrations(): void {
    // Construct the absolute path (easier than relative path)
    const path = `/${this.data.orgId}/integrations/configured-integrations`;
    this.router.navigate([path]);
  }
}
